import _ from 'lodash';
import {
  getInitialState,
  getPremiumBySumInsured,
  getSuggestedPremium,
  type PremiumPeriod,
} from '.';
import {
  type DiscountType,
  type CoveragePremium,
  type CoveragePremiums,
  type Premium,
} from 'dto/CoveragePremium';
import { CoverageProduct } from 'dto/marketableProduct';
import { type CoverageSelection } from 'state/utils/coverage-init';

export const getSelectablePremiums = (
  selectedCoverages: CoverageProduct[],
  coveragePremium: CoveragePremium
) => {
  // For non-life coverages, all the premiums are selectable
  if (coveragePremium.coverageProduct !== CoverageProduct.LIFE) {
    return coveragePremium.premiums;
  }
  // For life premiums, check for additional coverages to determine the selectables
  const lifeMinSumInsured = getInitialState().marketableProduct?.coverages.find(
    (c) => c.coverageProduct === CoverageProduct.LIFE
  )?.minSumInsured;
  if (!lifeMinSumInsured) {
    throw new Error('Min sum insured cannot be undefined for life insurance');
  }
  return selectedCoverages.includes(CoverageProduct.LIFE) &&
    selectedCoverages.length > 1
    ? coveragePremium.premiums
    : coveragePremium.premiums.filter((p) => p.sumInsured >= lifeMinSumInsured);
};

export const isPremiumSelectable = (
  coveragePremiums: CoveragePremium[],
  coverageProduct: CoverageProduct,
  selectedCoverages: CoverageProduct[],
  premium: Premium
) => {
  const coverage = coveragePremiums.find(
    (c) => c.coverageProduct === coverageProduct
  );
  if (!coverage) {
    return false;
  }
  const insuranceOptions = getSelectablePremiums(selectedCoverages, coverage);
  return insuranceOptions.some((obj) => _.isEqual(obj, premium));
};

export const getSuggestedOrMinSumInsuredPremium = (
  coveragePremiums: CoveragePremium[],
  coverageProduct: CoverageProduct,
  selectedCoverages: CoverageProduct[]
) => {
  const coverage = getInitialState().marketableProduct?.coverages.find(
    (c) => c.coverageProduct === coverageProduct
  );

  if (!coverage) {
    throw new Error(`No coverage found for coverageProduct ${coverageProduct}`);
  }

  const suggestedPremium = getSuggestedPremium(
    coveragePremiums,
    coverageProduct,
    coverage.preselectedDeductibleDays
  );
  if (!suggestedPremium) {
    throw new Error('suggested premium not found');
  }
  const isSuggestionSelectable = isPremiumSelectable(
    coveragePremiums,
    coverageProduct,
    selectedCoverages,
    suggestedPremium
  );
  if (isSuggestionSelectable) {
    return suggestedPremium;
  } else {
    return getPremiumBySumInsured(
      coveragePremiums,
      coverageProduct,
      coverage.minSumInsured,
      coverage.preselectedDeductibleDays
    );
  }
};

export const getDiscount = (
  premium: Premium,
  selectablePremiums: Premium[],
  discountType: DiscountType
) => {
  return selectablePremiums
    .find(
      (option) => option.sumInsured.toString() === premium.sumInsured.toString()
    )
    ?.discounts.find((discount) => discount.name === discountType);
};

export const getSelectedOption = (
  sumInsured: string,
  selectablePremiums: Premium[],
  deductibleDays?: string
) => {
  return selectablePremiums.find(
    (option) =>
      option.sumInsured.toString() === sumInsured &&
      option.deductibleDays?.toString() === deductibleDays
  );
};

export const computeSelectedPremiums = (
  coveragePremiums: CoveragePremium[] | undefined,
  coverageSelections: Record<CoverageProduct, CoverageSelection | undefined>
): CoveragePremiums => {
  const result: CoveragePremiums = {
    [CoverageProduct.LIFE]: undefined,
    [CoverageProduct.CRITICAL_ILLNESS]: undefined,
    [CoverageProduct.PERMANENT_DISABILITY]: undefined,
    [CoverageProduct.TEMPORARY_DISABILITY]: undefined,
  };

  Object.values(CoverageProduct).forEach((coverageProduct) => {
    const selection = coverageSelections[coverageProduct];

    if (!selection || !coveragePremiums) {
      result[coverageProduct] = undefined;
      return;
    }

    result[coverageProduct] = getPremiumBySumInsured(
      coveragePremiums,
      coverageProduct,
      selection.sumInsured,
      selection.deductibleDays
    );
  });

  return result;
};

export const calculatePremiumTotal = (
  selectedPremiums: CoveragePremiums,
  selectedCoverages: CoverageProduct[],
  premiumPeriod: PremiumPeriod
): number => {
  return selectedCoverages.reduce(
    (total: number, coverageProduct: CoverageProduct) => {
      const premium = selectedPremiums[coverageProduct];
      return total + (premium?.[premiumPeriod]?.amountInCents ?? 0);
    },
    0
  );
};
